import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, styled, Stack, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';

// Firebase Imports
import { db } from '../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

// --- Styled Components ---
const ContactHeroWrapper = styled(Box)({
    position: 'relative',
    padding: '160px 0 100px 0',
    background: '#020617',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
});

const BackgroundBlob = styled(motion.div)(({ color, size, top, left }) => ({
    position: 'absolute',
    width: size,
    height: size,
    background: color,
    filter: 'blur(120px)',
    borderRadius: '50%',
    opacity: 0.12,
    top: top,
    left: left,
    zIndex: 0,
}));

const InfoCard = styled(motion.div)({
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '20px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'rgba(45, 212, 191, 0.08)',
        borderColor: 'rgba(45, 212, 191, 0.3)',
        transform: 'translateY(-5px)'
    }
});

const ContactHero = () => {
    // 1. DYNAMIC DATA STATE
    const [contactDetails, setContactDetails] = useState({
        email: '',
        location: '',
        carrerStatus: ''
    });

    const [fetching, setFetching] = useState(true);

    // 2. FETCH DATA FROM FIRESTORE
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const docRef = doc(db, "adminSettings", "contactInfo");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setContactDetails(docSnap.data());
                    console.log("Fetched Contact Info:", docSnap.data());
                }
            } catch (err) {
                console.error("Fetch Error Hero:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchInfo();
    }, []);


    return (
        <ContactHeroWrapper>
            {/* Background Blobs restricted to this section */}
            <BackgroundBlob
                color="#3b82f6" size="400px" top="-10%" left="10%"
                animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.15, 0.1] }}
                transition={{ duration: 8, repeat: Infinity }}
            />
            <BackgroundBlob
                color="#2dd4bf" size="300px" top="50%" left="70%"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.08, 0.12, 0.08] }}
                transition={{ duration: 10, repeat: Infinity }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Stack spacing={6} alignItems="center">

                    {/* Title Section */}
                    <Box sx={{ textAlign: 'center' }}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography variant="overline" sx={{ color: '#2dd4bf', fontWeight: 900, letterSpacing: 5 }}>
                                GET IN TOUCH
                            </Typography>
                            <Typography variant="h1" sx={{
                                color: '#fff',
                                fontWeight: 900,
                                fontSize: { xs: '3rem', md: '5rem' },
                                mt: 1,
                                mb: 2,
                                background: 'linear-gradient(to right, #fff, #94a3b8)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}>
                                Let's Build Something <br />
                                <span style={{ color: '#2dd4bf', WebkitTextFillColor: '#2dd4bf' }}>Great Together.</span>
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: '600px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.8 }}>
                                Have a project in mind or just want to say hi? Feel free to reach out.
                                I'm always open to discussing new projects, creative ideas or original opportunities.
                            </Typography>
                        </motion.div>
                    </Box>

                    {/* Quick Contact Info Grid */}
                    <Box sx={{ width: '100%', mt: 4 }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={3}
                            justifyContent="center"
                        >
                            <InfoCard
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', display: 'flex' }}>
                                    <EmailIcon fontSize="large" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>EMAIL ME</Typography>
                                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                        {fetching ? <Skeleton width={150} sx={{ bgcolor: 'grey.900' }} /> : contactDetails.email}
                                    </Typography>
                                </Box>
                            </InfoCard>

                            <InfoCard
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(45, 212, 191, 0.1)', color: '#2dd4bf', display: 'flex' }}>
                                    <ChatIcon fontSize="large" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>LET'S CHAT</Typography>
                                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                        {fetching ? (
                                            <Skeleton width={120} sx={{ bgcolor: 'grey.900' }} />
                                        ) : (
                                            // Spelling changed from careerStatus to carrerStatus to match your Log/Firebase
                                            contactDetails.carrerStatus || "Available for Freelance"
                                        )} </Typography>
                                </Box>
                            </InfoCard>

                            <InfoCard
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(148, 163, 184, 0.1)', color: '#94a3b8', display: 'flex' }}>
                                    <LocationOnIcon fontSize="large" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>LOCATION</Typography>
                                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                        {fetching ? <Skeleton width={100} sx={{ bgcolor: 'grey.900' }} /> : contactDetails.location}
                                    </Typography>
                                </Box>
                            </InfoCard>
                        </Stack>
                    </Box>

                </Stack>
            </Container>
        </ContactHeroWrapper>
    );
};

export default ContactHero;